import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import WhatsAppPhoneInput from "../../components/WhatsAppPhoneInput";
import WhatsAppRecapSettings from "../../components/WhatsAppRecapSettings";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateBusinessResources } from "../../utils/businessResourceFreshness";
import {
  isValidOptionalWhatsAppPhone,
  WHATSAPP_PHONE_VALIDATION_MESSAGE,
} from "../../utils/whatsappPhone";
import AdminMobileShell from "./AdminMobileShell";
import {
  Summary, SearchBar, Chips, EmptyState, Fab, Sheet, ActionRow, DetailRow,
  Field, TextInput, TextAreaInput, SelectInput, Pill, Mono, Ico, fmt,
} from "../produits/productMobileKit";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";
import {
  deleteStaff, getStaff, listRoles, listStaff, listWarehouses, saveStaff,
  saveStaffCompanyRoles,
  listCompaniesForModals, suggestStaffUsername,
} from "./adminMobileApi";
import {
  copyCredential,
  generateSecurePassword,
} from "../../utils/staffPassword";

const PAGE_SIZE = 20;
const emptyForm = () => ({
  name: "", civility: "", phone: "", whatsapp_phone: "", email: "", status: "enabled", address: "",
  whatsapp_recap_warehouse_ids: [],
  role_ids: [], company_ids: [], warehouse_ids: [], password: "", password_confirmation: "", profile_image: null,
});
const errorMessage = (error, fallback) => error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

export default function StaffMobile() {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [unavailableRoleCompanyIds, setUnavailableRoleCompanyIds] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const formRef = useRef(form);
  formRef.current = form;
  const [saving, setSaving] = useState(false);
  const [companyRoleEditor, setCompanyRoleEditor] = useState(null);
  const [companyRoleIds, setCompanyRoleIds] = useState([]);
  const [companyRoleSaving, setCompanyRoleSaving] = useState(false);
  const [usernameSuggestionLoading, setUsernameSuggestionLoading] = useState(false);
  const [usernameSuggestionError, setUsernameSuggestionError] = useState("");
  const [usernameWasEdited, setUsernameWasEdited] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const inFlight = useRef(false);
  const editRequestRef = useRef(0);
  const currentCompanyRef = useRef(selectedCompany);
  currentCompanyRef.current = selectedCompany;
  useEffect(() => {
    editRequestRef.current += 1;
    setFormOpen(false);
    setEditing(null);
  }, [selectedCompany]);
  const usernameWasEditedRef = useRef(false);
  const usernameSuggestionRequestRef = useRef(0);
  const usernameSuggestionNameRef = useRef("");

  const canView = user?.is_superadmin || hasPermission("Admin.GestionUtilisateurs.view");
  const canCreate = user?.is_superadmin || hasPermission("Admin.GestionUtilisateurs.create");
  const canEdit = user?.is_superadmin || hasPermission("Admin.GestionUtilisateurs.edit");
  const canAssignRole = user?.is_superadmin || hasPermission("Admin.GestionUtilisateurs.assign_role");
  const canDelete = user?.is_superadmin || hasPermission("Admin.GestionUtilisateurs.delete");
  const canAssignSysAdmin = user?.is_superadmin || hasPermission("Admin.GestionUtilisateurs.assign_sysadmin");
  const isSysAdminActor = user?.is_superadmin === true || Number(user?.is_superadmin) === 1;

  const requestUsernameSuggestion = useCallback(async (name, { force = false } = {}) => {
    const normalizedName = String(name || "").trim();
    if (!isSysAdminActor || editing || !normalizedName) return;

    const requestId = usernameSuggestionRequestRef.current + 1;
    usernameSuggestionRequestRef.current = requestId;
    setUsernameSuggestionLoading(true);
    setUsernameSuggestionError("");
    try {
      const username = await suggestStaffUsername(normalizedName);
      if (requestId !== usernameSuggestionRequestRef.current) return;
      if (!force && usernameWasEditedRef.current) return;
      if (username) {
        usernameSuggestionNameRef.current = normalizedName;
        usernameWasEditedRef.current = false;
        setUsernameWasEdited(false);
        setForm((previous) => ({ ...previous, email: username }));
      }
    } catch (error) {
      if (requestId !== usernameSuggestionRequestRef.current) return;
      setUsernameSuggestionError(
        errorMessage(
          error,
          "La proposition automatique est indisponible. Saisissez l’identifiant manuellement.",
        ),
      );
    } finally {
      if (requestId === usernameSuggestionRequestRef.current) {
        setUsernameSuggestionLoading(false);
      }
    }
  }, [editing, isSysAdminActor]);

  useEffect(() => {
    if (!formOpen || editing || !isSysAdminActor || usernameWasEdited) return undefined;
    const normalizedName = String(form.name || "").trim();
    if (!normalizedName) {
      usernameSuggestionNameRef.current = "";
      setForm((previous) => previous.email ? { ...previous, email: "" } : previous);
      return undefined;
    }
    if (usernameSuggestionNameRef.current === normalizedName) return undefined;
    const timer = window.setTimeout(
      () => requestUsernameSuggestion(normalizedName),
      350,
    );
    return () => window.clearTimeout(timer);
  }, [
    editing,
    form.name,
    formOpen,
    isSysAdminActor,
    requestUsernameSuggestion,
    usernameWasEdited,
  ]);

  const handleSuggestUsername = () => {
    usernameWasEditedRef.current = false;
    requestUsernameSuggestion(form.name, { force: true });
  };

  const handleEmailChange = (email) => {
    usernameWasEditedRef.current = true;
    usernameSuggestionRequestRef.current += 1;
    setUsernameWasEdited(true);
    setUsernameSuggestionLoading(false);
    setUsernameSuggestionError("");
    setForm((previous) => ({ ...previous, email }));
  };

  const handleGeneratePassword = () => {
    try {
      const password = generateSecurePassword(16);
      setForm((previous) => ({
        ...previous,
        password,
        password_confirmation: password,
      }));
      setGeneratedPassword(password);
      setPasswordVisible(true);
      message.success("Mot de passe sécurisé généré");
    } catch (error) {
      message.error(error.message || "Impossible de générer le mot de passe");
    }
  };

  const handleCopyGeneratedPassword = async () => {
    try {
      await copyCredential(generatedPassword);
      message.success("Mot de passe copié");
    } catch (error) {
      message.warning("Copie automatique indisponible. Copiez-le manuellement.");
    }
  };

  const selectedRoleIsGlobalSysAdmin = useMemo(() => {
    return roles.some(
      (role) =>
        form.role_ids.includes(Number(role.id)) &&
        role.name === "SysAdmin" &&
        !role.company_id,
    );
  }, [form.role_ids, roles]);

  const companyById = useMemo(
    () => new Map(companies.map((company) => [Number(company.id), company])),
    [companies],
  );

  const warehousesByCompany = useMemo(() => {
    const grouped = new Map();
    warehouses.forEach((warehouse) => {
      const companyId = Number(warehouse.company_id);
      if (!grouped.has(companyId)) grouped.set(companyId, []);
      grouped.get(companyId).push(warehouse);
    });
    return grouped;
  }, [warehouses]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  const load = useCallback(async (nextPage = 1, append = false) => {
    if (!selectedCompany || !canView) return;
    setLoading(true);
    try {
      const result = await listStaff({
        companyId: selectedCompany, page: nextPage, limit: PAGE_SIZE,
        search: debouncedQuery, status,
      });
      setRows((previous) => append ? [...previous, ...result.rows] : result.rows);
      setTotal(result.total || result.rows.length);
      setPage(nextPage);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger le personnel"));
      if (!append) setRows([]);
    } finally { setLoading(false); }
  }, [canView, debouncedQuery, selectedCompany, status]);

  const loadWarehouses = useCallback(async (companyIds) => {
    const normalizedCompanyIds = [...new Set(
      (Array.isArray(companyIds) ? companyIds : [companyIds])
        .map(Number)
        .filter((companyId) => Number.isInteger(companyId) && companyId > 0),
    )];
    const results = await Promise.allSettled(
      normalizedCompanyIds.map(async (companyId) => {
        const firstPage = await listWarehouses(companyId, 1);
        if (firstPage.pages <= 1) return firstPage.rows;
        const extraPages = await Promise.all(
          Array.from({ length: firstPage.pages - 1 }, (_, index) =>
            listWarehouses(companyId, index + 2),
          ),
        );
        return [...firstPage.rows, ...extraPages.flatMap((result) => result.rows)];
      }),
    );
    const companyWarehouseRows = results
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value);
    if (companyWarehouseRows.length !== results.length) {
      message.warning(
        "Certains établissements ne sont plus accessibles. Retirez l’entreprise concernée si nécessaire.",
      );
    }
    const rows = companyWarehouseRows
      .flat()
      .filter((warehouse) => warehouse.status === "active");
    setWarehouses(rows);
    return rows;
  }, []);

  const loadReferences = useCallback(async (companyIds = [selectedCompany]) => {
    if (!selectedCompany) return;
    try {
      const normalizedCompanyIds = [...new Set(companyIds.map(Number).filter((id) => id > 0))];
      const [roleResults, companyRows] = await Promise.all([
        Promise.allSettled(normalizedCompanyIds.map(listRoles)),
        listCompaniesForModals("all"),
      ]);
      const roleById = new Map();
      roleResults.filter((result) => result.status === "fulfilled")
        .flatMap((result) => result.value)
        .forEach((role) => roleById.set(Number(role.id), role));
      setUnavailableRoleCompanyIds(normalizedCompanyIds.filter((_, index) => roleResults[index].status === "rejected"));
      setRoles((previous) => {
        previous.filter((role) => formRef.current.role_ids.includes(Number(role.id)))
          .forEach((role) => roleById.set(Number(role.id), role));
        return [...roleById.values()].filter((role) => canAssignSysAdmin || role.name !== "SysAdmin" || role.company_id);
      });
      setCompanies(companyRows);
      await loadWarehouses(companyIds);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger les rôles, entreprises et établissements"));
    }
  }, [canAssignSysAdmin, loadWarehouses, selectedCompany]);

  useEffect(() => { load(1, false); }, [load]);
  useEffect(() => { loadReferences([selectedCompany]); }, [loadReferences, selectedCompany]);
  useEffect(() => {
    if (!formOpen || selectedRoleIsGlobalSysAdmin || !selectedCompany) return;

    const primaryCompanyId = Number(selectedCompany);
    setForm((previous) => {
      if (previous.company_ids.includes(primaryCompanyId)) return previous;
      return {
        ...previous,
        company_ids: [primaryCompanyId, ...previous.company_ids],
      };
    });
  }, [formOpen, selectedCompany, selectedRoleIsGlobalSysAdmin]);

  const enabledCount = useMemo(() => rows.filter((row) => row.status === "enabled").length, [rows]);

  const openDetail = async (row) => {
    setDetail(row);
    if (Number(row.company_id) !== Number(selectedCompany)) return;
    setDetailLoading(true);
    try { setDetail(await getStaff(row.id)); }
    catch (error) { message.error(errorMessage(error, "Impossible de charger les détails")); }
    finally { setDetailLoading(false); }
  };

  const openForm = async (row = null) => {
    const editRequestId = ++editRequestRef.current;
    const editCompanyId = selectedCompany;
    if (row && Number(row.company_id) !== Number(selectedCompany)) {
      message.warning("Modifiez le profil de cette entreprise depuis la fiche du collaborateur.");
      return;
    }
    usernameWasEditedRef.current = Boolean(row);
    usernameSuggestionRequestRef.current += 1;
    usernameSuggestionNameRef.current = "";
    setUsernameWasEdited(Boolean(row));
    setUsernameSuggestionLoading(false);
    setUsernameSuggestionError("");
    setGeneratedPassword("");
    setPasswordVisible(false);
    setDetail(null);
    setEditing(row);
    if (!row) {
      await loadReferences([selectedCompany]);
      if (editRequestId !== editRequestRef.current || editCompanyId !== currentCompanyRef.current) return;
      setForm({
        ...emptyForm(),
        company_ids: selectedCompany ? [Number(selectedCompany)] : [],
      });
      setFormOpen(true);
      return;
    }
    try {
      const data = await getStaff(row.id);
      if (editRequestId !== editRequestRef.current || editCompanyId !== currentCompanyRef.current) return;
      const assignedCompanyIds = [...new Set([
        Number(data.company_id),
        ...(data.assigned_companies || []).map((company) => Number(company.id)),
        ...(data.assigned_warehouses || []).map((warehouse) => Number(warehouse.company_id)),
      ])].filter((companyId) => Number.isInteger(companyId) && companyId > 0);
      if (data.access_scope !== "global") {
        await loadReferences(assignedCompanyIds);
      } else if (!roles.length || !companies.length) {
        await loadReferences([selectedCompany]);
      }
      setRoles((previous) => [...new Map([
        ...previous,
        ...(data.roles || []),
      ].map((role) => [Number(role.id), role])).values()]);
      if (editRequestId !== editRequestRef.current || editCompanyId !== currentCompanyRef.current) return;
      setForm({
        name: data.name || "", phone: data.phone || "", email: data.email || "",
        civility: data.civility || "",
        whatsapp_phone: data.whatsapp_phone || "",
        whatsapp_recap_warehouse_ids: data.whatsapp_recap_warehouse_ids || [],
        status: data.status || "enabled", address: data.address || "",
        role_ids: (data.role_ids || (data.role_id ? [data.role_id] : [])).map(Number),
        company_ids: data.access_scope === "global" ? [] : assignedCompanyIds,
        warehouse_ids: (data.assigned_warehouses || []).map((warehouse) => Number(warehouse.id)),
        password: "", password_confirmation: "", profile_image: null,
      });
      setEditing(data);
      setFormOpen(true);
    } catch (error) { message.error(errorMessage(error, "Impossible de préparer la modification")); }
  };

  const closeForm = () => {
    editRequestRef.current += 1;
    usernameSuggestionRequestRef.current += 1;
    setUsernameSuggestionLoading(false);
    setUsernameSuggestionError("");
    setGeneratedPassword("");
    setPasswordVisible(false);
    setForm(emptyForm());
    setFormOpen(false);
    setEditing(null);
  };

  const toggleWarehouse = (id) => setForm((previous) => ({
    ...previous,
    warehouse_ids: previous.warehouse_ids.includes(id)
      ? previous.warehouse_ids.filter((warehouseId) => warehouseId !== id)
      : [...previous.warehouse_ids, id],
  }));

  const toggleRole = (id) => {
    const roleId = Number(id);
    const selectedRole = roles.find((role) => Number(role.id) === roleId);
    const isGlobalSysAdmin =
      selectedRole?.name === "SysAdmin" && !selectedRole.company_id;

    setForm((previous) => {
      if (isGlobalSysAdmin) {
        return { ...previous, role_ids: [roleId] };
      }
      const withoutSysAdmin = previous.role_ids.filter((currentRoleId) => {
        const role = roles.find(
          (candidate) => Number(candidate.id) === Number(currentRoleId),
        );
        return !(role?.name === "SysAdmin" && !role.company_id);
      });
      return {
        ...previous,
        role_ids: withoutSysAdmin.includes(roleId)
          ? withoutSysAdmin.filter((currentRoleId) => currentRoleId !== roleId)
          : [...withoutSysAdmin, roleId],
      };
    });
  };

  const toggleCompany = async (id) => {
    const companyId = Number(id);
    if (companyId === Number(selectedCompany)) return;

    const nextCompanyIds = form.company_ids.includes(companyId)
      ? form.company_ids.filter((currentId) => currentId !== companyId)
      : [...form.company_ids, companyId];
    const allowedCompanyIds = new Set(nextCompanyIds);
    setForm((previous) => ({
      ...previous,
      company_ids: nextCompanyIds,
      role_ids: previous.role_ids.filter((roleId) => {
        const role = roles.find((item) => Number(item.id) === Number(roleId));
        return role && (!role.company_id || allowedCompanyIds.has(Number(role.company_id)));
      }),
      warehouse_ids: previous.warehouse_ids.filter((warehouseId) => {
        const warehouse = warehouses.find(
          (item) => Number(item.id) === Number(warehouseId),
        );
        return warehouse && allowedCompanyIds.has(Number(warehouse.company_id));
      }),
    }));

    try {
      await loadReferences(nextCompanyIds);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger les établissements"));
    }
  };

  const openCompanyRoleEditor = async (row) => {
    try {
      const companyRoles = await listRoles(selectedCompany);
      setRoles(companyRoles);
      setCompanyRoleIds((row.roles || [])
        .filter((role) => Number(role.company_id) === Number(selectedCompany))
        .map((role) => Number(role.id)));
      setCompanyRoleEditor(row);
      setDetail(null);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger les rôles de cette entreprise"));
    }
  };

  const saveCompanyRoles = async () => {
    if (!companyRoleEditor || !companyRoleIds.length) {
      message.warning("Sélectionnez au moins un rôle pour cette entreprise");
      return;
    }
    setCompanyRoleSaving(true);
    try {
      await saveStaffCompanyRoles(companyRoleEditor.id, selectedCompany, companyRoleIds);
      message.success("Profil de l’entreprise mis à jour");
      setCompanyRoleEditor(null);
      await load(1, false);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de mettre à jour ce profil"));
    } finally {
      setCompanyRoleSaving(false);
    }
  };

  const submit = async () => {
    if (inFlight.current) return;
    if (!isValidOptionalWhatsAppPhone(form.whatsapp_phone)) {
      message.warning(WHATSAPP_PHONE_VALIDATION_MESSAGE);
      return;
    }
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.role_ids.length || (!selectedRoleIsGlobalSysAdmin && !form.warehouse_ids.length)) {
      message.warning("Renseignez les champs obligatoires et au moins un établissement");
      return;
    }
    if (!selectedRoleIsGlobalSysAdmin) {
      const missingRoleCompanyId = form.company_ids.find((companyId) =>
        !roles.some((role) => Number(role.company_id) === Number(companyId) &&
          form.role_ids.includes(Number(role.id))),
      );
      if (missingRoleCompanyId) {
        message.warning(`Choisissez un profil pour ${companyById.get(Number(missingRoleCompanyId))?.name || "chaque entreprise"}`);
        return;
      }
      const selectedWarehouseCompanies = new Set(
        warehouses
          .filter((warehouse) => form.warehouse_ids.includes(Number(warehouse.id)))
          .map((warehouse) => Number(warehouse.company_id)),
      );
      const missingCompanyId = form.company_ids.find(
        (companyId) => !selectedWarehouseCompanies.has(Number(companyId)),
      );
      if (missingCompanyId) {
        message.warning(
          `Sélectionnez au moins un établissement pour ${companyById.get(Number(missingCompanyId))?.name || "chaque entreprise"}`,
        );
        return;
      }
    }
    if (!editing && form.password.length < 6) {
      message.warning("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (form.password && form.password !== form.password_confirmation) {
      message.warning("Les mots de passe ne correspondent pas");
      return;
    }
    inFlight.current = true;
    setSaving(true);
    try {
      const data = new FormData();
      data.append("whatsapp_recap_company_id", String(selectedCompany));
      ["name", "civility", "phone", "whatsapp_phone", "email", "status", "address"].forEach((key) => data.append(key, form[key]));
      data.append("whatsapp_recap_warehouse_ids", JSON.stringify(form.whatsapp_recap_warehouse_ids));
      form.role_ids.forEach((id) => data.append("role_ids[]", String(id)));
      if (!selectedRoleIsGlobalSysAdmin) {
        form.company_ids.forEach((id) => data.append("company_ids[]", String(id)));
        form.warehouse_ids.forEach((id) => data.append("warehouse_ids[]", String(id)));
      }
      if (form.password) {
        data.append("password", form.password);
        data.append("password_confirmation", form.password_confirmation);
      }
      if (form.profile_image instanceof File) data.append("profile_image", form.profile_image);
      data.append("user_type", "staff_members");
      data.append("company_id", String(editing?.company_id || selectedCompany));
      await saveStaff(editing?.id, data);
      invalidateBusinessResources(queryClient, {
        userId: user?.id, companyId: selectedCompany, warehouseId: null, moduleScope: "admin",
      }, "staff-whatsapp-settings");
      message.success(editing ? "Utilisateur mis à jour" : "Utilisateur créé");
      closeForm();
      await load(1, false);
    } catch (error) { message.error(errorMessage(error, "Impossible d'enregistrer l'utilisateur")); }
    finally { inFlight.current = false; setSaving(false); }
  };

  const remove = (row) => Modal.confirm({
    title: "Supprimer cet utilisateur ?", content: row.name, okText: "Supprimer", okType: "danger", cancelText: "Annuler", centered: true,
    onOk: async () => {
      try { await deleteStaff(row.id); message.success("Utilisateur supprimé"); setDetail(null); await load(1, false); }
      catch (error) { message.error(errorMessage(error, "Suppression impossible")); }
    },
  });

  if (!canView) return <AdminMobileShell title="Utilisateurs"><div className="body"><EmptyState icon="alert-circle" title="Accès non autorisé" /></div></AdminMobileShell>;
  if (!selectedCompany) return <AdminMobileShell title="Utilisateurs"><div className="body"><EmptyState icon="store" title="Entreprise requise" text="Sélectionnez une entreprise." /></div></AdminMobileShell>;

  return (
    <AdminMobileShell title="Utilisateurs" subtitle={`${fmt(total)} membre${total > 1 ? "s" : ""}`}>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[{ label: "Total", value: fmt(total) }, { label: "Actifs chargés", value: fmt(enabledCount) }]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Nom, email ou téléphone…" />
        <Chips value={status} onChange={setStatus} options={[{ key: "all", label: "Tous" }, { key: "enabled", label: "Activés" }, { key: "disabled", label: "Désactivés" }]} />
      </div>
      <div className="body">
        {loading && !rows.length ? <EmptyState loading title="Chargement…" /> : !rows.length ? <EmptyState icon="users" title="Aucun utilisateur" /> : (
          <div className="list">
            {rows.map((row) => <button key={row.id} className="rcard" onClick={() => openDetail(row)}>
              <Mono name={row.name} square />
              <div className="rcard__main"><div className="rcard__name">{row.name}</div><div className="rcard__meta"><Pill tone={row.status === "enabled" ? "ok" : "off"} label={row.status === "enabled" ? "Activé" : "Désactivé"} /><span className="rcard__sub">{row.role_names?.join(", ") || row.role_name || row.email}</span></div></div>
              <span className="rcard__chev"><Ico name="chevron-right" /></span>
            </button>)}
            {rows.length < total ? <button className="btn btn--ghost" onClick={() => load(page + 1, true)} disabled={loading}>{loading ? "Chargement…" : `Charger plus (${rows.length}/${total})`}</button> : null}
          </div>
        )}
      </div>
      {canCreate ? <Fab label="Nouvel utilisateur" onClick={() => openForm()} /> : null}

      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.name} subtitle={detailLoading ? "Chargement…" : detail?.role_names?.join(", ") || detail?.role_name}>
        {detail ? <>
          <DetailRow label="Statut" value={detail.status === "enabled" ? "Activé" : "Désactivé"} />
          <DetailRow label="Email" value={detail.email || "—"} /><DetailRow label="Téléphone" value={detail.phone || "—"} />
          <DetailRow label="WhatsApp" value={detail.whatsapp_phone || "Non renseigné"} />
          <DetailRow label="Adresse" value={detail.address || "—"} /><DetailRow label="Rôles" value={detail.role_names?.join(", ") || detail.role_name || "—"} />
          <DetailRow label="Établissements" value={(detail.assigned_warehouses || []).map((warehouse) => `${warehouse.company_name ? `${warehouse.company_name} · ` : ""}${warehouse.name}`).join(", ") || "—"} />
          {canAssignRole && !detail.is_superadmin && Number(detail.id) !== Number(user?.id)
            ? <ActionRow icon="shield" label="Profil de cette entreprise" onClick={() => openCompanyRoleEditor(detail)} /> : null}
          {canEdit && Number(detail.company_id) === Number(selectedCompany) ? <ActionRow icon="pencil" label="Modifier" onClick={() => openForm(detail)} /> : null}
          {canDelete && Number(detail.company_id) === Number(selectedCompany) ? <ActionRow icon="trash-2" label="Supprimer" danger onClick={() => remove(detail)} /> : null}
        </> : null}
      </Sheet>

      <Sheet open={Boolean(companyRoleEditor)} onClose={() => setCompanyRoleEditor(null)} title="Profil de cette entreprise" subtitle={companyRoleEditor?.name}>
        <div className="field__hint" style={{ marginBottom: 8 }}>Choisissez au moins un rôle. Les profils des autres entreprises restent inchangés.</div>
        {roles.filter((role) => Number(role.company_id) === Number(selectedCompany)).map((role) => (
          <button key={role.id} type="button" className="actsheet__row" onClick={() => setCompanyRoleIds((previous) => previous.includes(Number(role.id)) ? previous.filter((id) => id !== Number(role.id)) : [...previous, Number(role.id)])}>
            <Ico name={companyRoleIds.includes(Number(role.id)) ? "check-circle-2" : "circle"} />{role.name}
          </button>
        ))}
        <div className="sheet__actions">
          <button className="btn btn--ghost" onClick={() => setCompanyRoleEditor(null)}>Annuler</button>
          <button className="btn btn--primary" onClick={saveCompanyRoles} disabled={companyRoleSaving || !companyRoleIds.length}>{companyRoleSaving ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </Sheet>

      <Sheet open={formOpen} onClose={closeForm} title={editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}>
        <Field label="Civilité" hint="Facultative">
          <SelectInput value={form.civility} placeholder="Non renseignée"
            options={[{ value: "mr", label: "M." }, { value: "ms", label: "Mme" }]}
            onChange={(civility) => setForm((previous) => ({ ...previous, civility: civility || "" }))} />
        </Field>
        <Field label="Nom *"><TextInput value={form.name} onChange={(name) => setForm((f) => ({ ...f, name }))} autoFocus /></Field>
        <Field label="Téléphone *"><TextInput value={form.phone} onChange={(phone) => setForm((f) => ({ ...f, phone }))} inputMode="tel" /></Field>
        <Field label="Numéro WhatsApp" hint="Facultatif. Choisissez ci-dessous les établissements des bilans.">
          <WhatsAppPhoneInput
            aria-label="Numéro WhatsApp"
            disabled={saving}
            phone={form.phone}
            value={form.whatsapp_phone}
            onChange={(whatsapp_phone) => setForm((previous) => ({ ...previous, whatsapp_phone }))}
          />
        </Field>
        <Field label="Établissements des bilans WhatsApp" hint="Entreprise sélectionnée. Laissez vide pour désactiver la réception.">
          <WhatsAppRecapSettings
            active={formOpen}
            actorId={user?.id}
            recipientId={editing?.id}
            aria-label="Établissements des bilans WhatsApp"
            assignedIds={form.warehouse_ids}
            companyId={selectedCompany}
            disabled={saving}
            isGlobal={selectedRoleIsGlobalSysAdmin}
            onChange={(whatsapp_recap_warehouse_ids) => setForm((previous) => ({ ...previous, whatsapp_recap_warehouse_ids }))}
            savedWarehouses={editing?.whatsapp_recap_warehouses || []}
            value={form.whatsapp_recap_warehouse_ids}
          />
        </Field>
        <Field
          label="Identifiant de connexion *"
          hint={!editing && isSysAdminActor ? "proposé en @elsagestion.com, modifiable" : null}
        >
          <TextInput
            autoComplete="email"
            inputMode="email"
            onChange={handleEmailChange}
            placeholder="prenom.nom@elsagestion.com"
            value={form.email}
          />
        </Field>
        {!editing && isSysAdminActor ? <>
          {usernameSuggestionError ? <div className="field__hint" style={{ color: "var(--red, #c53f47)", marginTop: -6 }}>{usernameSuggestionError}</div> : null}
          <button
            className="btn btn--ghost"
            disabled={usernameSuggestionLoading || !form.name.trim()}
            onClick={handleSuggestUsername}
            type="button"
          >
            <Ico name="refresh-cw" />
            {usernameSuggestionLoading ? "Recherche…" : "Proposer un identifiant disponible"}
          </button>
        </> : null}
        <Field label="Statut *"><SelectInput value={form.status} onChange={(value) => setForm((f) => ({ ...f, status: value }))} options={[{ value: "enabled", label: "Activé" }, { value: "disabled", label: "Désactivé" }]} /></Field>
        <Field label="Adresse"><TextAreaInput value={form.address} onChange={(address) => setForm((f) => ({ ...f, address }))} rows={2} /></Field>
        {canAssignSysAdmin ? roles.filter((role) => role.name === "SysAdmin" && !role.company_id).map((role) =>
          <button key={role.id} type="button" className="actsheet__row" onClick={() => toggleRole(role.id)}><Ico name={selectedRoleIsGlobalSysAdmin ? "check-circle-2" : "circle"} />Accès SysAdmin global</button>,
        ) : null}
        {selectedRoleIsGlobalSysAdmin ? (
          <div className="empty" style={{ padding: 16, marginTop: 12 }}>
            <strong>Accès global</strong>
            <div>Toutes les entreprises et tous les établissements.</div>
          </div>
        ) : <>
          <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Entreprises accessibles *</div>
          <div className="field__hint" style={{ marginBottom: 8 }}>L’entreprise principale ne peut pas être retirée.</div>
          {companies
            .filter((company) => {
              const companyId = Number(company.id);
              const isUsable = ["active", "trial"].includes(
                String(company.status || "active").toLowerCase(),
              );
              return isUsable || form.company_ids.includes(companyId);
            })
            .map((company) => {
              const companyId = Number(company.id);
              const isPrimary = companyId === Number(selectedCompany);
              const isSelected = form.company_ids.includes(companyId);
              const isUsable = ["active", "trial"].includes(
                String(company.status || "active").toLowerCase(),
              );
              return <button key={company.id} type="button" className="actsheet__row" disabled={isPrimary || (!isUsable && !isSelected)} onClick={() => toggleCompany(companyId)}><Ico name={isSelected ? "check-circle-2" : "circle"} />{company.name}{isPrimary ? " · principale" : ""}{!isUsable ? " · inactive" : ""}</button>;
            })}
          <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Profils par entreprise *</div>
          <div className="field__hint" style={{ marginBottom: 8 }}>Choisissez au moins un rôle dans chaque entreprise. Leurs permissions se cumulent uniquement dans cette entreprise.</div>
          {form.company_ids.map((companyId) => <React.Fragment key={`roles-${companyId}`}>
            <div className="field__lbl" style={{ marginTop: 12 }}>{companyById.get(Number(companyId))?.name || `Entreprise ${companyId}`}</div>
            {unavailableRoleCompanyIds.includes(Number(companyId)) ? <div className="field__hint">Profil consultable ici. Basculez sur cette entreprise pour le modifier.</div> : null}
            {roles.filter((role) => Number(role.company_id) === Number(companyId)).map((role) =>
              <button key={role.id} type="button" className="actsheet__row" disabled={unavailableRoleCompanyIds.includes(Number(companyId))} onClick={() => toggleRole(role.id)}><Ico name={form.role_ids.includes(Number(role.id)) ? "check-circle-2" : "circle"} />{role.name}</button>,
            )}
            {!roles.some((role) => Number(role.company_id) === Number(companyId)) ? <div className="field__hint">Aucun rôle disponible pour cette entreprise.</div> : null}
          </React.Fragment>)}
          <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Établissements autorisés *</div>
          <div className="field__hint" style={{ marginBottom: 8 }}>Choisissez au moins un établissement par entreprise.</div>
          {form.company_ids.map((companyId) => <React.Fragment key={companyId}>
            <div className="field__lbl" style={{ marginTop: 12 }}>{companyById.get(Number(companyId))?.name || `Entreprise ${companyId}`}</div>
            {(warehousesByCompany.get(Number(companyId)) || []).map((warehouse) => <button key={warehouse.id} type="button" className="actsheet__row" onClick={() => toggleWarehouse(Number(warehouse.id))}><Ico name={form.warehouse_ids.includes(Number(warehouse.id)) ? "check-circle-2" : "circle"} />{warehouse.name}</button>)}
          </React.Fragment>)}
        </>}
        <Field label={editing ? "Nouveau mot de passe" : "Mot de passe *"} hint={editing ? "laisser vide pour conserver" : "6 caractères minimum"}><div className="ctrl"><input type={passwordVisible ? "text" : "password"} value={form.password} onChange={(event) => { const password = event.target.value; if (password !== generatedPassword) setGeneratedPassword(""); setForm((f) => ({ ...f, password })); }} /></div></Field>
        <Field label="Confirmer le mot de passe"><div className="ctrl"><input type={passwordVisible ? "text" : "password"} value={form.password_confirmation} onChange={(event) => setForm((f) => ({ ...f, password_confirmation: event.target.value }))} /></div></Field>
        {!editing && isSysAdminActor ? (
          <div className="sheet__actions" style={{ flexWrap: "wrap", marginTop: 4 }}>
            <button className="btn btn--ghost" onClick={handleGeneratePassword} type="button"><Ico name="key-round" />Générer</button>
            {generatedPassword ? <button className="btn btn--ghost" onClick={handleCopyGeneratedPassword} type="button"><Ico name="copy" />Copier</button> : null}
            {form.password ? <button className="btn btn--ghost" onClick={() => setPasswordVisible((visible) => !visible)} type="button"><Ico name={passwordVisible ? "eye-off" : "eye"} />{passwordVisible ? "Masquer" : "Afficher"}</button> : null}
          </div>
        ) : null}
        <Field label="Photo de profil" hint="optionnel"><div className="ctrl"><input type="file" accept="image/*" onChange={(event) => setForm((f) => ({ ...f, profile_image: event.target.files?.[0] || null }))} /></div></Field>
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={closeForm}>Annuler</button><button className="btn btn--primary" onClick={submit} disabled={saving}>{saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}</button></div>
      </Sheet>
    </AdminMobileShell>
  );
}
